import {Box, BoxProps, Divider, Sheet, SheetProps, Stack, useTheme} from "@mui/joy";
import {PropsWithChildren, ReactNode, useMemo, useRef, useState} from "react";
import {useVirtualizer} from "@tanstack/react-virtual";
import {useResizeObserver} from "@react-hookz/web";
import {figureColors} from "../color.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBomb, faSpinner} from "@fortawesome/pro-regular-svg-icons";
import {HCBox} from "./HCBox.tsx";
import {ActionButtonProps, buildActionButtons} from "./ActionButton.tsx";
import {mergeSx} from "../components/joyx.tsx";
import {Keyed} from "../util/arutil.tsx";
import {JoyableColor} from "./JoyableColor.tsx";

const TILE_WIDTH_PIXELS = 260;
const TILE_HGAP_PIXELS = 8;
const TILE_VGAP_PIXELS = 2;
const TILE_INSET_PIXELS = 2;
// export const TILE_HEIGHT = "auto";

const TILE_SX = {
    width: TILE_WIDTH_PIXELS,
    overflowX: "clip",
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
} as const;

const TILE_CONTENT_SX = {
    overflowX: "clip",
    px: `${TILE_INSET_PIXELS.toString()}px`,
    py: `${TILE_INSET_PIXELS.toString()}px`,
    flex: 1, display: "flex", flexDirection: "column",
} as const;

export const TILE_ACTION_BUTTON_SX = {
    py: 1,
    flex: 1,
};

export function buildTileActionButtons(kprops: Keyed<ActionButtonProps>[] | undefined) {
    return buildActionButtons({py: 1.2, px: 1.2, flex: 1}, kprops);
}

function tileSpreadTilesPerRow(totalWidth: number) {
    let rv = Math.floor((totalWidth - TILE_HGAP_PIXELS) / (TILE_WIDTH_PIXELS + TILE_HGAP_PIXELS));
    if (rv < 1) {
        rv = 1;
    }
    return rv;
}

// export function FlexwrapTileSpread({children}: PropsWithChildren) {
//     return <Box sx={{
//         display: "flex", flexDirection: "row", flexWrap: "wrap", width: "94%",
//         columnGap: "6px", rowGap: "6px", justifyContent: "stretch", pl: 1,
//     }}>{children}</Box>
// }

export interface TileSpreadProps<FoodT> {
    foods: FoodT[];
    Component: ({food}: { food: FoodT }) => ReactNode;
    estimatedHeight: number;
}

export function TileSpread<FoodT>({foods, Component, estimatedHeight}: TileSpreadProps<FoodT>) {
    const hostRef = useRef<HTMLDivElement>(null);
    const [hostWidth, setHostWidth] = useState(0);
    useResizeObserver(hostRef, ({contentRect}) => {
        const hw = contentRect.width;
        setHostWidth(hw);
    }, true);
    const {ncols, nrows} = useMemo(() => {
        const ncols = hostWidth ? tileSpreadTilesPerRow(hostWidth) : 1;
        const nrows = Math.floor((foods.length - 1) / ncols) + 1;
        return {ncols, nrows};
    }, [foods.length, hostWidth]);

    const virtualizer = useVirtualizer({
        count: nrows,
        getScrollElement: () => {
            const rv = hostRef.current;
            return rv;
        },
        estimateSize: () => {
            return estimatedHeight;
        }
    });

    const vitems = virtualizer.getVirtualItems();
    return <Box ref={hostRef}>
        <Box sx={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
        }}>
            <Box sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${(vitems[0]?.start ?? 0).toString()}px)`,
            }}>
                {vitems.map(vrow => {
                    const rix = vrow.index;
                    const rsix = rix * ncols;
                    // the last row may be truncated - don't overrun the end
                    const remaining = foods.length - rsix;
                    const wantedCols = Math.min(remaining, ncols);
                    const cixen = Array.from(Array(wantedCols).keys());
                    const ixen = cixen.map(cix => rsix + cix);
                    return <Stack direction="row"
                                  sx={{
                                      px: `${TILE_HGAP_PIXELS.toString()}px`,
                                      py: `${TILE_VGAP_PIXELS.toString()}px`,
                                      justifyContent: "space-evenly", alignItems: "stretch",
                                      flex: 1,
                                  }}
                                  key={vrow.key}
                                  data-index={vrow.index}
                                  ref={virtualizer.measureElement}
                    >
                        {ixen.map((ix) => foods[ix]
                            ? <Component food={foods[ix]} key={ix}/>
                            : null
                        )}
                    </Stack>
                })}
            </Box>
        </Box>
    </Box>
}

// function tileVariantSpecificSx(
//     {theme, color, variant: inVariant}: { theme: Theme, color?: CardProps["color"], variant?: CardProps["variant"] }) {
//     const paletteName = color ?? "neutral";
//     const palette = theme.palette[paletteName];
//     const variant = inVariant ?? "outlined";
//     switch (variant) {
//         case "outlined":
//             return {
//                 borderWidth: 3,
//                 borderColor: palette.outlinedBorder,
//                 borderRadius: "12px",
//             } as const;
//         default:
//             return {
//                 margin: 2,
//             } as const;
//     }
// }


export interface JoyableProps {
    color?: JoyableColor;
    variant?: SheetProps["variant"];
    invertedColors?: SheetProps["invertedColors"];
    sx?: SheetProps["sx"];
}

export type TileSubjectProps = Omit<BoxProps, "children">;

export interface TileProps extends PropsWithChildren<JoyableProps> {
    Subject?: (props: TileSubjectProps) => ReactNode;
    actions?: ReactNode;
}

export function Tile({
                         Subject,
                         actions,
                         children,
                         sx: explicitSx,
                         variant: explicitVariant,
                         ...rest
                     }: TileProps) {
    const {color, invertedColors} = rest;
    // const oyaVariant = explicitVariant ?? "outlined";
    const oyaVariant = "outlined";
    const theme = useTheme();
    const colors = figureColors({
        theme, color, variant: oyaVariant, invertedColors
    });
    const paletteName = color ?? "neutral";
    const palette = theme.palette[paletteName];
    const variantSpecific = oyaVariant === "outlined"
        ? ({
            borderWidth: 3,
            borderColor: palette.outlinedBorder,
            borderRadius: "12px",
        } as const)
        : ({
            margin: 2,
        } as const);
    const sx = mergeSx(
        TILE_SX,
        variantSpecific,
        explicitSx,
    );

    return <Sheet {...rest} color={color} variant={oyaVariant} sx={sx}>
        <Box sx={TILE_CONTENT_SX}>
            <Box sx={{
                backgroundColor: colors.mainBackground,
                color: colors.mainForeground,
                overflowX: "clip",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}>
                {Subject
                    ? <HCBox sx={{
                        color: colors.subjectForeground,
                        backgroundColor: colors.subjectBackground,
                        mx: 0.3,
                    }}>
                        <Subject sx={{
                            px: 1,
                            py: 0.6,
                        }}/>
                    </HCBox>
                    : null}
                {children}
                {actions
                    ? <><Divider sx={{my: 0.6, backgroundColor: colors.mainForeground}}/>
                        <Stack direction="row" sx={{
                            alignItems: "center",
                            justifyContent: "space-evenly"
                        }}>
                            {actions}
                        </Stack>
                    </>
                    : null}
            </Box>
        </Box>
    </Sheet>
}

export function SuspendedTile(props: Partial<TileProps>) {
    return <Tile {...props}>
        <FontAwesomeIcon icon={faSpinner} spin={true} size="2x"/>;
    </Tile>;
}

export function BombTile(props: Partial<TileProps>) {
    return <Tile {...props} color="danger" variant="solid">
        <FontAwesomeIcon icon={faBomb} size="2x"/>
    </Tile>
}
